import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, Loader, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ordinal(n) {
  if (n >= 11 && n <= 13) return `${n}th`;
  return `${n}${{ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th"}`;
}
function fmtDate(str) {
  if (!str) return "";
  const [, mo, d] = str.split("-").map(Number);
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const [y] = str.split("-");
  return `${ordinal(d)} ${months[mo - 1]} ${y}`;
}

export default function InviteAccept() {
  const [params] = useSearchParams();
  const token = params.get("token");

  const [state, setState] = useState("loading"); // loading | confirmed | already | error | expired
  const [info, setInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const calledRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setState("error");
      setErrorMsg("No invite token found in this link.");
      return;
    }
    if (calledRef.current) return;
    calledRef.current = true;
    acceptInvite();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  async function acceptInvite() {
    try {
      const { data } = await axios.post(`${API}/invite/accept`, { token });
      setInfo(data);
      setState(data.already_processed ? "already" : "confirmed");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Something went wrong.";
      if (status === 410) {
        setState("expired");
        setErrorMsg(detail);
      } else if (status === 409) {
        setState("error");
        setErrorMsg(detail);
      } else {
        setState("error");
        setErrorMsg(detail);
      }
    }
  }

  return (
    <div style={{ fontFamily: "Arial, sans-serif", minHeight: "100vh", backgroundColor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
      <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.08)", padding: "48px 40px", maxWidth: "480px", width: "100%", textAlign: "center" }}>
        {/* Logo */}
        <img
          src="https://customer-assets-jai6qajn.emergentagent.net/job_session-manager-61/artifacts/oiyq9sc2_Dynamo%20Logo%5B4147%5D.png"
          alt="Dynamo Gymnastics"
          style={{ width: "120px", marginBottom: "32px" }}
        />

        {/* Loading */}
        {state === "loading" && (
          <>
            <Loader size={48} style={{ color: "#1656a4", animation: "spin 1s linear infinite", marginBottom: "16px" }} />
            <p style={{ color: "#64748b", fontSize: "15px" }}>Confirming your place…</p>
          </>
        )}

        {/* Confirmed */}
        {(state === "confirmed" || state === "already") && info && (
          <>
            <CheckCircle size={56} style={{ color: "#16a34a", marginBottom: "16px" }} />
            <h1 style={{ fontSize: "22px", fontWeight: "bold", color: "#111827", margin: "0 0 8px" }}>
              {state === "already" ? "Already Confirmed!" : "You're Confirmed!"}
            </h1>
            <p style={{ color: "#374151", fontSize: "15px", lineHeight: "1.6", margin: "0 0 24px" }}>
              Great news — <strong>{info.child_first_name}</strong>'s taster session is confirmed for:
            </p>
            <div style={{ backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "10px", padding: "20px", marginBottom: "24px" }}>
              <p style={{ margin: "0 0 6px", fontSize: "14px", color: "#15803d", fontWeight: "bold" }}>
                {info.class_day}
              </p>
              <p style={{ margin: "0 0 4px", fontSize: "18px", color: "#111827", fontWeight: "bold" }}>
                {fmtDate(info.taster_date)}
              </p>
              <p style={{ margin: "0", fontSize: "16px", color: "#374151" }}>
                at {info.taster_time}
              </p>
            </div>
            <p style={{ color: "#6b7280", fontSize: "13px" }}>
              We look forward to welcoming {info.child_first_name} to Dynamo Gymnastics!
            </p>
          </>
        )}

        {/* Expired */}
        {state === "expired" && (
          <>
            <AlertTriangle size={56} style={{ color: "#f59e0b", marginBottom: "16px" }} />
            <h1 style={{ fontSize: "20px", fontWeight: "bold", color: "#111827", margin: "0 0 8px" }}>
              Link Expired
            </h1>
            <p style={{ color: "#6b7280", fontSize: "14px", lineHeight: "1.6" }}>
              {errorMsg}
            </p>
            <p style={{ color: "#6b7280", fontSize: "13px", marginTop: "16px" }}>
              Please contact us on <strong>02380 455007</strong> or email{" "}
              <a href="mailto:admin@dynamogymnastics.co.uk" style={{ color: "#1656a4" }}>admin@dynamogymnastics.co.uk</a>
            </p>
          </>
        )}

        {/* Error */}
        {state === "error" && (
          <>
            <XCircle size={56} style={{ color: "#dc2626", marginBottom: "16px" }} />
            <h1 style={{ fontSize: "20px", fontWeight: "bold", color: "#111827", margin: "0 0 8px" }}>
              Something went wrong
            </h1>
            <p style={{ color: "#6b7280", fontSize: "14px", lineHeight: "1.6" }}>
              {errorMsg}
            </p>
            <p style={{ color: "#6b7280", fontSize: "13px", marginTop: "16px" }}>
              Please contact us on <strong>02380 455007</strong>
            </p>
          </>
        )}

        {/* Footer */}
        <p style={{ marginTop: "40px", fontSize: "11px", color: "#9ca3af", borderTop: "1px solid #f1f5f9", paddingTop: "16px" }}>
          Dynamo Gymnastics · Hamble Sports Complex · Satchell Lane, SO31 4NE
        </p>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
